import type { StatusPillTone } from '@/components/ui/status-pill-select';
import type { VacancyStatus } from '@/types/recruitment';
import { isAxiosError } from 'axios';

export const VACANCY_STATUS_LABELS: Record<VacancyStatus, string> = {
  DRAFT: 'Draft',
  UNDER_REVIEW: 'Under review',
  ACTIVE: 'Active',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
};

export const VACANCY_STATUS_TONE: Record<VacancyStatus, StatusPillTone> = {
  DRAFT: 'neutral',
  UNDER_REVIEW: 'warning',
  ACTIVE: 'success',
  PAUSED: 'neutral',
  CANCELLED: 'danger',
  CLOSED: 'outline',
  ARCHIVED: 'outline',
};

// Mirrors vacancies.service.ts ALLOWED_STATUS_TRANSITIONS.
// Any non-ARCHIVED status can transition to any other non-ARCHIVED status.
// ARCHIVED is entered via /archive and exited via /restore — not via the dropdown.
export const VACANCY_ALLOWED_TRANSITIONS: Record<VacancyStatus, VacancyStatus[]> = {
  DRAFT: ['UNDER_REVIEW', 'ACTIVE', 'PAUSED', 'CANCELLED', 'CLOSED'],
  UNDER_REVIEW: ['DRAFT', 'ACTIVE', 'PAUSED', 'CANCELLED', 'CLOSED'],
  ACTIVE: ['DRAFT', 'UNDER_REVIEW', 'PAUSED', 'CANCELLED', 'CLOSED'],
  PAUSED: ['DRAFT', 'UNDER_REVIEW', 'ACTIVE', 'CANCELLED', 'CLOSED'],
  CANCELLED: ['DRAFT', 'UNDER_REVIEW', 'ACTIVE', 'PAUSED', 'CLOSED'],
  CLOSED: ['DRAFT', 'UNDER_REVIEW', 'ACTIVE', 'PAUSED', 'CANCELLED'],
  ARCHIVED: [],
};

export const VACANCY_STATUS_MENU_ORDER: VacancyStatus[] = [
  'DRAFT',
  'UNDER_REVIEW',
  'ACTIVE',
  'PAUSED',
  'CANCELLED',
  'CLOSED',
];

export const VACANCY_STATUS_CONFIRM_COPY: Partial<
  Record<VacancyStatus, { title: string; description: string; action: string }>
> = {
  CLOSED: {
    title: 'Close this vacancy?',
    description:
      'Closing marks the vacancy as finished (hired / no longer hiring). You can still reopen it afterwards, but this is typically a final state.',
    action: 'Close vacancy',
  },
  CANCELLED: {
    title: 'Cancel this vacancy?',
    description:
      'Cancelling marks the vacancy as discontinued. You can still reopen it afterwards, but this is typically a final state.',
    action: 'Cancel vacancy',
  },
};

export function extractVacancyStatusErrorCode(err: unknown): string | null {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const body = err.response.data as { error?: unknown };
    if (typeof body.error === 'string') return body.error;
  }
  return null;
}
